import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCog,
  FaKey,
  FaEnvelope,
  FaSearch,
  FaRobot,
  FaShareAlt,
  FaServer,
  FaChevronDown,
  FaChevronUp,
  FaSave,
  FaUndo,
  FaCheckCircle,
  FaExclamationTriangle,
  FaWallet,
  FaRocket,
  FaSpinner,
} from "react-icons/fa";
import api, { endpoints } from "@/api/config";
import toast from "react-hot-toast";

interface ConfigSection {
  key: string;
  title: string;
  icon: React.ReactNode;
  fields: ConfigField[];
}

interface ConfigField {
  key: string;
  label: string;
  type: "text" | "password" | "number" | "select" | "textarea" | "switch";
  value: string | number | boolean;
  options?: { label: string; value: string }[];
  placeholder?: string;
  description?: string;
}

type ConfigurationState = {
  apiKeys: {
    openai_api_key: string;
    tavily_api_key: string;
    inboxkit_api_key: string;
    email_list_verify_api_key: string;
  };
  email: {
    max_words: number;
    max_revisions: number;
    include_html_tags: boolean;
    temperature: number;
  };
  search: {
    default_mode: string;
    max_results_per_query: number;
    max_total_results: number;
    search_depth: string;
    include_linkedin: boolean;
    days_for_news: number;
    days_for_company: number;
  };
  models: {
    default_model: string;
    advanced_model: string;
    search_model: string;
    search_mini_model: string;
    deep_research_model: string;
  };
  socialMedia: {
    facebook_app_id: string;
    facebook_app_secret: string;
    tiktok_client_key: string;
    tiktok_client_secret: string;
  };
  system: {
    environment: string;
    base_url: string;
    max_content_length: number;
    session_lifetime: number;
  };
};

const ConfigurationsTab: React.FC = () => {
  const [accordions, setAccordions] = useState({
    apiKeys: true,
    email: true,
    search: true,
    models: true,
    socialMedia: true,
    system: true,
    wallet: true,
  });

  const [configs, setConfigs] = useState<ConfigurationState | null>(null);
  const [saveStatus, setSaveStatus] = useState<{
    [key: string]: "idle" | "saving" | "success" | "error";
  }>({});
  const [hasChanges, setHasChanges] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [walletData, setWalletData] = useState<{
    remaining: number;
    used: number;
    total: number;
  } | null>(null);
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [creditAmount, setCreditAmount] = useState<number>(25);

  const toggleAccordion = (key: keyof typeof accordions) => {
    setAccordions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFieldChange = (
    section: keyof ConfigurationState,
    field: string,
    value: string | number | boolean,
  ) => {
    setConfigs((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      };
    });
    setHasChanges((prev) => ({ ...prev, [section]: true }));
    setSaveStatus((prev) => ({ ...prev, [section]: "idle" }));
  };

  const fetchConfigurations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(endpoints.adminConfigurations);
      if (!data?.success) {
        throw new Error(data?.error || "Failed to load configurations");
      }

      setConfigs(data.configurations);
      setHasChanges({});
      setSaveStatus({});
    } catch (err) {
      console.error("Error fetching configurations:", err);
      const message =
        err instanceof Error ? err.message : "Failed to load configurations";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleSave = async (section: keyof ConfigurationState) => {
    if (!configs) return;

    setSaveStatus((prev) => ({ ...prev, [section]: "saving" }));

    try {
      const payload = {
        [section]: configs[section],
      };

      const { data } = await api.put(endpoints.adminConfigurations, payload);
      if (!data?.success) {
        throw new Error(data?.error || "Failed to save configuration");
      }

      setSaveStatus((prev) => ({ ...prev, [section]: "success" }));
      setHasChanges((prev) => ({ ...prev, [section]: false }));
      toast.success("Configuration saved successfully!");

      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [section]: "idle" }));
      }, 2000);
    } catch (error) {
      console.error("Error saving configuration:", error);
      setSaveStatus((prev) => ({ ...prev, [section]: "error" }));
      toast.error("Failed to save configuration");
      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [section]: "idle" }));
      }, 2000);
    }
  };

  const handleReset = (section: keyof ConfigurationState) => {
    fetchConfigurations();
    setHasChanges((prev) => ({ ...prev, [section]: false }));
    setSaveStatus((prev) => ({ ...prev, [section]: "idle" }));
  };

  const fetchBalance = async () => {
    setIsCheckingBalance(true);
    try {
      const { data } = await api.get("/api/inboxkit/balance");
      if (data.success) {
        setWalletData({
          remaining: data.credits_remaining,
          used: data.credits_used,
          total: data.total_credits,
        });
      }
    } catch (err) {
      toast.error("Failed to fetch balance");
    } finally {
      setIsCheckingBalance(false);
    }
  };

  const handleAddCredits = async () => {
    try {
      const sessionId = `config_${Date.now()}`;
      localStorage.setItem(sessionId, JSON.stringify({ activeTab: "system" }));

      const returnUrl = `${window.location.origin}${window.location.pathname}`;

      const { data } = await api.post("/api/inboxkit/add-credits", {
        amount: creditAmount,
        redirect_url: returnUrl,
      });

      if (data.payment_link) {
        window.location.href = data.payment_link;
      }
    } catch (err) {
      toast.error("Could not initiate payment");
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  const configSections: ConfigSection[] = configs
    ? [
        {
          key: "apiKeys",
          title: "API Keys & Credentials",
          icon: <FaKey />,
          fields: [
            {
              key: "openai_api_key",
              label: "OpenAI API Key",
              type: "password",
              value: configs.apiKeys.openai_api_key,
              description: "API key for OpenAI services",
            },
            {
              key: "tavily_api_key",
              label: "Tavily API Key",
              type: "password",
              value: configs.apiKeys.tavily_api_key,
              description: "API key for Tavily search services",
            },
            {
              key: "inboxkit_api_key",
              label: "InboxKit API Key",
              type: "password",
              value: configs.apiKeys.inboxkit_api_key,
              description: "API key for InboxKit email services",
            },
            {
              key: "email_list_verify_api_key",
              label: "Email List Verify API Key",
              type: "password",
              value: configs.apiKeys.email_list_verify_api_key,
              description: "API key for email verification services",
            },
          ],
        },
        {
          key: "email",
          title: "Email Configuration",
          icon: <FaEnvelope />,
          fields: [
            {
              key: "max_words",
              label: "Max Words",
              type: "number",
              value: configs.email.max_words,
              description: "Maximum words in generated emails",
            },
            {
              key: "max_revisions",
              label: "Max Revisions",
              type: "number",
              value: configs.email.max_revisions,
              description: "Maximum number of email revisions allowed",
            },
            {
              key: "include_html_tags",
              label: "Include HTML Tags",
              type: "switch",
              value: configs.email.include_html_tags,
              description: "Include HTML formatting in emails",
            },
            {
              key: "temperature",
              label: "Temperature",
              type: "number",
              value: configs.email.temperature,
              description: "AI model temperature (0-1)",
            },
          ],
        },
        {
          key: "search",
          title: "Search Configuration",
          icon: <FaSearch />,
          fields: [
            {
              key: "default_mode",
              label: "Default Search Mode",
              type: "select",
              value: configs.search.default_mode,
              options: [
                { label: "Original", value: "original" },
                { label: "OpenAI", value: "openai" },
                { label: "Hybrid", value: "hybrid" },
              ],
              description: "Default search mode for lead discovery",
            },
            {
              key: "max_results_per_query",
              label: "Max Results Per Query",
              type: "number",
              value: configs.search.max_results_per_query,
              description: "Maximum results returned per search query",
            },
            {
              key: "max_total_results",
              label: "Max Total Results",
              type: "number",
              value: configs.search.max_total_results,
              description: "Maximum total results across all queries",
            },
            {
              key: "search_depth",
              label: "Search Depth",
              type: "select",
              value: configs.search.search_depth,
              options: [
                { label: "Basic", value: "basic" },
                { label: "Advanced", value: "advanced" },
              ],
              description: "Depth of search analysis",
            },
            {
              key: "include_linkedin",
              label: "Include LinkedIn",
              type: "switch",
              value: configs.search.include_linkedin,
              description: "Include LinkedIn in search results",
            },
            {
              key: "days_for_news",
              label: "Days for News",
              type: "number",
              value: configs.search.days_for_news,
              description: "Number of days to look back for news",
            },
            {
              key: "days_for_company",
              label: "Days for Company",
              type: "number",
              value: configs.search.days_for_company,
              description: "Number of days to look back for company data",
            },
          ],
        },
        {
          key: "models",
          title: "AI Model Configuration",
          icon: <FaRobot />,
          fields: [
            {
              key: "default_model",
              label: "Default Model",
              type: "select",
              value: configs.models.default_model,
              options: [
                { label: "GPT-5 Mini", value: "gpt-5-mini" },
                { label: "GPT-4", value: "gpt-4-0125-preview" },
                { label: "GPT-4o", value: "gpt-4o" },
              ],
              description: "Default AI model for general tasks",
            },
            {
              key: "advanced_model",
              label: "Advanced Model",
              type: "select",
              value: configs.models.advanced_model,
              options: [
                { label: "GPT-4 Preview", value: "gpt-4-0125-preview" },
                { label: "GPT-4o", value: "gpt-4o" },
                { label: "O4 Mini", value: "o4-mini" },
              ],
              description: "Model for advanced tasks",
            },
            {
              key: "search_model",
              label: "Search Model",
              type: "select",
              value: configs.models.search_model,
              options: [
                { label: "GPT-4o Search", value: "gpt-4o-search-preview" },
                {
                  label: "GPT-4o Mini Search",
                  value: "gpt-4o-mini-search-preview",
                },
              ],
              description: "Model for search operations",
            },
            {
              key: "search_mini_model",
              label: "Search Mini Model",
              type: "select",
              value: configs.models.search_mini_model,
              options: [
                {
                  label: "GPT-4o Mini Search",
                  value: "gpt-4o-mini-search-preview",
                },
                { label: "GPT-4o Search", value: "gpt-4o-search-preview" },
              ],
              description: "Lightweight model for search",
            },
            {
              key: "deep_research_model",
              label: "Deep Research Model",
              type: "select",
              value: configs.models.deep_research_model,
              options: [
                {
                  label: "O4 Mini Deep Research",
                  value: "o4-mini-deep-research",
                },
                { label: "GPT-4", value: "gpt-4-0125-preview" },
              ],
              description: "Model for deep research tasks",
            },
          ],
        },
        {
          key: "socialMedia",
          title: "Social Media Configuration",
          icon: <FaShareAlt />,
          fields: [
            {
              key: "facebook_app_id",
              label: "Facebook App ID",
              type: "text",
              value: configs.socialMedia.facebook_app_id,
              description: "Facebook application ID for OAuth",
            },
            {
              key: "facebook_app_secret",
              label: "Facebook App Secret",
              type: "password",
              value: configs.socialMedia.facebook_app_secret,
              description: "Facebook application secret key",
            },
            {
              key: "tiktok_client_key",
              label: "TikTok Client Key",
              type: "text",
              value: configs.socialMedia.tiktok_client_key,
              description: "TikTok client key for API access",
            },
            {
              key: "tiktok_client_secret",
              label: "TikTok Client Secret",
              type: "password",
              value: configs.socialMedia.tiktok_client_secret,
              description: "TikTok client secret key",
            },
          ],
        },
        {
          key: "system",
          title: "System Settings",
          icon: <FaServer />,
          fields: [
            {
              key: "environment",
              label: "Environment",
              type: "select",
              value: configs.system.environment,
              options: [
                { label: "Production", value: "production" },
                { label: "Development", value: "development" },
                { label: "Staging", value: "staging" },
              ],
              description: "Application environment",
            },
            {
              key: "base_url",
              label: "Base URL",
              type: "text",
              value: configs.system.base_url,
              placeholder: "https://example.com",
              description: "Base URL for the application",
            },
            {
              key: "max_content_length",
              label: "Max Content Length (MB)",
              type: "number",
              value: configs.system.max_content_length,
              description: "Maximum upload size in megabytes",
            },
            {
              key: "session_lifetime",
              label: "Session Lifetime (Days)",
              type: "number",
              value: configs.system.session_lifetime,
              description: "Session expiration time in days",
            },
          ],
        },
      ]
    : [];

  if (loading || !configs) {
    return (
      <div className="configurations-tab">
        <div className="config-loading">
          <div className="loading-spinner" />
          <p>Loading configurations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="configurations-tab">
        <div className="config-error">
          <p>{error}</p>
          <button onClick={fetchConfigurations} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const renderField = (
    field: ConfigField,
    section: keyof ConfigurationState,
  ) => {
    if (!configs) return null;
    const fieldValue =
      configs[section][
        field.key as keyof ConfigurationState[keyof ConfigurationState]
      ];

    switch (field.type) {
      case "switch":
        return (
          <div className="config-field switch-field">
            <div className="field-header">
              <label className="field-label">{field.label}</label>
              {field.description && (
                <span className="field-description">{field.description}</span>
              )}
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={fieldValue as boolean}
                onChange={(e) =>
                  handleFieldChange(section, field.key, e.target.checked)
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        );

      case "select":
        return (
          <div className="config-field">
            <div className="field-header">
              <label className="field-label">{field.label}</label>
              {field.description && (
                <span className="field-description">{field.description}</span>
              )}
            </div>
            <select
              className="config-input"
              value={fieldValue as string}
              onChange={(e) =>
                handleFieldChange(section, field.key, e.target.value)
              }
            >
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case "textarea":
        return (
          <div className="config-field">
            <div className="field-header">
              <label className="field-label">{field.label}</label>
              {field.description && (
                <span className="field-description">{field.description}</span>
              )}
            </div>
            <textarea
              className="config-input"
              value={fieldValue as string}
              onChange={(e) =>
                handleFieldChange(section, field.key, e.target.value)
              }
              placeholder={field.placeholder}
              rows={4}
            />
          </div>
        );

      default:
        return (
          <div className="config-field">
            <div className="field-header">
              <label className="field-label">{field.label}</label>
              {field.description && (
                <span className="field-description">{field.description}</span>
              )}
            </div>
            <input
              type={field.type}
              className="config-input"
              value={fieldValue as string | number}
              onChange={(e) => {
                const value =
                  field.type === "number"
                    ? parseFloat(e.target.value) || 0
                    : e.target.value;
                handleFieldChange(section, field.key, value);
              }}
              placeholder={field.placeholder}
            />
          </div>
        );
    }
  };

  return (
    <div className="configurations-tab">
      {/* InboxKit Wallet Balance */}
      <div className="tab-section">
        <div className="section-heading-row">
          <div>
            <h3 className="section-title">InboxKit Wallet Balance</h3>
            <p className="section-description">
              Add an extra 1000 messages for a one-time fee.
            </p>
          </div>
          <button
            className="btn btn-outline-secondary"
            onClick={handleAddCredits}
          >
            <FaRocket /> Purchase Credits
          </button>
        </div>
        <div className="balance-amount">
          {walletData !== null ? `$${walletData.remaining.toFixed(2)}` : "—"}
        </div>
        {walletData && (
          <div className="balance-stats-sub">
            <span>
              <strong>Used:</strong> ${walletData.used.toFixed(2)}
            </span>
            <span className="separator">|</span>
            <span>
              <strong>Total:</strong> ${walletData.total.toFixed(2)}
            </span>
          </div>
        )}
      </div>

      <div className="section-divider" />

      {/* Credits & Usage */}
      <div className="tab-section">
        <div className="section-heading-row">
          <div className="config-card-title-row">
            <h3 className="section-title">Credits & Usage</h3>
            <span className="plan-badge">Free Plan</span>
          </div>
          <button
            className="btn btn-outline-secondary"
            onClick={fetchBalance}
            disabled={isCheckingBalance}
          >
            {isCheckingBalance ? <FaSpinner className="spin" /> : null} Refresh
          </button>
        </div>
        <p className="section-description">
          Add an extra 1000 messages for a one-time fee.
        </p>
        <div className="usage-stats-grid">
          <div className="usage-stat">
            <div className="usage-stat-bar-row">
              <div className="usage-bar-fill" style={{ width: "74%" }}></div>
            </div>
            <div className="usage-stat-footer">
              <span className="stat-label">Emails / Day</span>
              <span className="stat-value">37/50</span>
            </div>
          </div>
          <div className="usage-stat">
            <div className="usage-stat-bar-row">
              <div className="usage-bar-fill" style={{ width: "100%" }}></div>
            </div>
            <div className="usage-stat-footer">
              <span className="stat-label">Campaigns / Month</span>
              <span className="stat-value">1/1</span>
            </div>
          </div>
          <div className="usage-stat">
            <div className="usage-stat-bar-row">
              <div className="usage-bar-fill" style={{ width: "5%" }}></div>
            </div>
            <div className="usage-stat-footer">
              <span className="stat-label">Leads / Month</span>
              <span className="stat-value">1/20</span>
            </div>
          </div>
        </div>
      </div>

      {/* All config sections */}
      {configSections.map((section) => (
        <React.Fragment key={section.key}>
          <div className="section-divider" />
          <div className="tab-section">
            <h3 className="section-title">{section.title}</h3>
            <div className="config-fields-grid">
              {section.fields.map((field) => (
                <div
                  key={field.key}
                  className={
                    field.type === "switch"
                      ? "config-field-full"
                      : "config-field-cell"
                  }
                >
                  {renderField(field, section.key as keyof ConfigurationState)}
                </div>
              ))}
            </div>
            <div className="config-actions">
              <button
                className="btn btn-outline-secondary"
                onClick={() =>
                  handleReset(section.key as keyof ConfigurationState)
                }
                disabled={!hasChanges[section.key]}
              >
                <FaUndo /> Reset
              </button>
              <button
                className="btn btn-primary"
                onClick={() =>
                  handleSave(section.key as keyof ConfigurationState)
                }
                disabled={
                  !hasChanges[section.key] ||
                  saveStatus[section.key] === "saving"
                }
              >
                {saveStatus[section.key] === "saving" && (
                  <FaSpinner className="spin" />
                )}
                {saveStatus[section.key] === "success" && <FaCheckCircle />}
                {saveStatus[section.key] === "error" && (
                  <FaExclamationTriangle />
                )}
                {saveStatus[section.key] === "saving"
                  ? "Saving..."
                  : saveStatus[section.key] === "success"
                    ? "Saved!"
                    : saveStatus[section.key] === "error"
                      ? "Error"
                      : "Save Changes"}
              </button>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default ConfigurationsTab;
